import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/pages/About";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { contactApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Mail, ShieldCheck, Users, Building2, Newspaper, CheckCircle2 } from "lucide-react";

const CATEGORIES = [
  "General enquiry",
  "Applicant support",
  "Supplier / partner",
  "Media / press",
  "Billing",
  "Founding Organization code request",
];

const FOUNDING_MESSAGE =
  "Hello WECH-RB team,\n\nWe are interested in becoming a WECH-RB Founding Organization and would like to request a Founding Organization code. Please share eligibility details and benefits.\n\nCompany: \nWebsite: \nContact person: ";

const AUDIENCES = [
  { icon: Users, t: "Applicants & suppliers", d: "Questions about applying, documents, or your certification status." },
  { icon: Building2, t: "Partners", d: "Explore collaboration, referrals, or programme partnerships." },
  { icon: Newspaper, t: "Media & press", d: "Interviews, statements, and brand assets." },
  { icon: ShieldCheck, t: "Verification", d: "Confirm the authenticity of a WECH-RB certificate." },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", category: "General enquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [params] = useSearchParams();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (params.get("topic") === "founding") {
      setForm((f) => ({
        ...f,
        category: "Founding Organization code request",
        message: f.message || FOUNDING_MESSAGE,
      }));
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await contactApi.send(form);
      setSent(true);
      toast.success(data.message || "Message sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Contact" title="Get in touch with WECH-RB"
        subtitle="Whether you're an applicant, supplier, partner, or member of the media, we're here to help. Send us a message and our team will respond promptly." />

      <section className="wechr-section">
        <div className="wechr-container grid gap-10 lg:grid-cols-[1.1fr,0.9fr]">
          {/* Form */}
          <Card>
            <CardContent className="p-6 sm:p-8">
              {sent ? (
                <div className="flex flex-col items-center gap-4 py-10 text-center" data-testid="contact-success">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-wgreen-100"><CheckCircle2 className="h-7 w-7 text-wgreen-700" /></div>
                  <div>
                    <p className="font-heading text-lg font-semibold text-navy-900">Message sent</p>
                    <p className="mt-1 max-w-sm text-sm text-[var(--wechr-muted-text)]">Thank you for reaching out. Our team will get back to you shortly at the email you provided.</p>
                  </div>
                  <Button variant="outline" onClick={() => { setSent(false); setForm({ name: "", email: "", organization: "", category: "General enquiry", message: "" }); }} data-testid="contact-send-another">Send another message</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                  <h2 className="font-heading text-lg font-semibold text-navy-900">Send us a message</h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><Label htmlFor="c-name">Full name</Label><Input id="c-name" required value={form.name} onChange={set("name")} className="mt-1" data-testid="contact-name-input" /></div>
                    <div><Label htmlFor="c-email">Email</Label><Input id="c-email" type="email" required value={form.email} onChange={set("email")} className="mt-1" data-testid="contact-email-input" /></div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><Label htmlFor="c-org">Organization <span className="text-[var(--wechr-muted-text)]">(optional)</span></Label><Input id="c-org" value={form.organization} onChange={set("organization")} className="mt-1" data-testid="contact-org-input" /></div>
                    <div>
                      <Label>Reason for contact</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger className="mt-1" data-testid="contact-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="c-msg">Message</Label>
                    <Textarea id="c-msg" required rows={6} value={form.message} onChange={set("message")} className="mt-1" placeholder="How can we help?" data-testid="contact-message-input" />
                  </div>
                  <Button type="submit" className="btn-green" disabled={loading} data-testid="contact-submit-button">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send message
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Info */}
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-wgreen-100"><Mail className="h-5 w-5 text-wgreen-700" /></div>
                  <div>
                    <p className="font-heading text-sm font-semibold text-navy-900">Email us directly</p>
                    <a href="mailto:contact.wech@wechllc.com" className="text-sm text-wgreen-700 hover:underline" data-testid="contact-email-link">contact.wech@wechllc.com</a>
                  </div>
                </div>
                <p className="mt-3 text-sm text-[var(--wechr-muted-text)]">We typically respond within 1–2 business days. For certificate authenticity, you can also use our public <a href="/verify" className="text-wgreen-700 hover:underline">verification page</a>.</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <p className="mb-4 font-heading text-sm font-semibold text-navy-900">Who we help</p>
                <div className="space-y-4">
                  {AUDIENCES.map((a) => (
                    <div key={a.t} className="flex items-start gap-3">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--wechr-subtle)]"><a.icon className="h-4 w-4 text-navy-700" /></div>
                      <div>
                        <p className="text-sm font-medium text-navy-900">{a.t}</p>
                        <p className="text-xs text-[var(--wechr-muted-text)]">{a.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
